// =============================================================================
// services/e2e.ts — TRUE end-to-end encryption for CRYB direct messages (text).
//
// Scheme: X25519 (Curve25519) key agreement + NaCl secretbox (XSalsa20-Poly1305).
//   - Each device generates a long-lived X25519 keypair on first use. The PRIVATE
//     key NEVER leaves the device (stored in the secure-store adapter / Keychain).
//   - The PUBLIC key is published to the backend (POST /users/me/e2e-key).
//   - To message a peer we fetch THEIR public key and compute a shared secret with
//     nacl.box.before(theirPub, myPriv). Curve25519 is symmetric, so the peer
//     computes the identical secret from (myPub, theirPriv) — both sides can read.
//   - Messages are sealed with secretbox(text, nonce, sharedKey) and serialized as
//     a self-describing marker: "e2e:v1:<nonceB64>:<ciphertextB64>".
//
// GRACEFUL by design: every public function is crash-safe. If the peer has not
// enabled E2E, or any crypto/storage/network step fails, callers fall back to
// plaintext or show a locked placeholder — sending/reading is NEVER blocked.
//
// Backend contract (verified live):
//   POST /users/me/e2e-key { publicKey }        — store my X25519 public key
//   GET  /users/:idOrUsername/e2e-key           — { data: { userId, publicKey|null } }
//
// MIGRATION (bare RN, P3 DMs / blueprint §1 swaps #7,#8,#15): the crypto + the
// PUBLIC API are IDENTICAL to the Expo version (nacl box/secretbox, the marker
// formats, every exported function). Only the three Expo module touch-points are
// swapped behind adapters:
//   - expo-crypto.getRandomBytes  → tweetnacl PRNG fed by the global
//     crypto.getRandomValues that `react-native-get-random-values` installs
//     (imported once at app entry / index.js, ahead of this module).
//   - expo-secure-store           → services/secure-store (keychain adapter).
//   - expo-file-system/legacy     → services/fs (react-native-fs adapter; lazy +
//     graceful so this module type-checks + runs before the pod is installed —
//     any fs failure falls back to the plaintext media path).
// =============================================================================
import 'react-native-get-random-values'; // installs global.crypto.getRandomValues
import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import * as SecureStore from './secure-store';
import * as FileSystem from './fs';
import { api, uploadEncrypted } from './api';

const { encodeBase64, decodeBase64, decodeUTF8, encodeUTF8 } = naclUtil;

// --- PRNG wiring -------------------------------------------------------------
// tweetnacl needs a CSPRNG for key + nonce generation. In bare RN we feed it the
// global crypto.getRandomValues that `react-native-get-random-values` polyfills
// (imported at the top of this file + at the app entry, before any crypto runs).
// This replaces expo-crypto.getRandomBytes(n) with the standard Web Crypto fill,
// keeping the SAME (x, n) => fill-x contract setPRNG expects. If the polyfill is
// somehow absent we throw rather than emit weak/zero key material (fail-closed for
// key generation; callers already treat a throw as "no E2E" → plaintext).
function fillRandom(x: Uint8Array, n: number): void {
  const g: any = globalThis as any;
  if (g?.crypto?.getRandomValues) {
    g.crypto.getRandomValues(x.subarray(0, n));
    return;
  }
  throw new Error('[e2e] no secure RNG available (react-native-get-random-values not loaded)');
}
nacl.setPRNG(fillRandom);

// `getRandomBytes(n)` shim — returns n secure random bytes (used for temp-file
// name tokens below). Mirrors the old expo-crypto.getRandomBytes signature.
function getRandomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  fillRandom(out, n);
  return out;
}

// --- Marker format -----------------------------------------------------------
const MARKER_PREFIX = 'e2e:v1:';

// A NaCl secretbox nonce is exactly nacl.secretbox.nonceLength (24) bytes. In our
// marker it's standard base64 with padding, so its encoded length is fixed. We
// pre-validate the length + charset before decode so a truncated/tampered marker
// is a clean 'malformed' failure instead of a confusing auth-fail or a throw.
const NONCE_B64_LEN = Math.ceil(nacl.secretbox.nonceLength / 3) * 4; // 24 → 32
const BASE64_RE = /^[A-Za-z0-9+/]+={0,2}$/;

/** True if `s` is a plausible base64 encoding of a 24-byte secretbox nonce. */
function isValidNonceB64(s: unknown): s is string {
  return typeof s === 'string' && s.length === NONCE_B64_LEN && BASE64_RE.test(s);
}

/** True if `s` is one of our encrypted message markers. */
export function isEncrypted(s: unknown): s is string {
  return typeof s === 'string' && s.startsWith(MARKER_PREFIX);
}

// --- Key storage -------------------------------------------------------------
const PRIV_KEY = 'e2e_priv';
const PUB_KEY = 'e2e_pub';

// In-memory cache of this device's keypair (base64) to avoid repeated SecureStore
// reads, and of derived shared keys per peer (key agreement is pure, so caching
// is safe for the app session).
let cachedPriv: string | null = null;
let cachedPub: string | null = null;
const sharedKeyCache = new Map<string, Uint8Array>();

/**
 * Clear all in-memory E2E caches (keypair + shared keys). Call on logout so a
 * subsequent login on the same process never leaks the previous user's key
 * material into the new session. (api.ts.logout lazy-requires THIS function.)
 */
export function clearE2ECache(): void {
  cachedPriv = null;
  cachedPub = null;
  sharedKeyCache.clear();
}

/**
 * Ensure this device has an X25519 keypair and that its PUBLIC key is published
 * to the backend. Idempotent and crash-safe — safe to fire on every DM open.
 *
 * Returns the device public key (base64) on success, or null if keys could not
 * be established (caller then simply operates without E2E, i.e. plaintext).
 */
export async function ensureKeypair(): Promise<string | null> {
  try {
    if (!cachedPriv || !cachedPub) {
      cachedPriv = await SecureStore.getItemAsync(PRIV_KEY);
      cachedPub = await SecureStore.getItemAsync(PUB_KEY);
    }

    if (!cachedPriv || !cachedPub) {
      // First run on this device: generate a fresh keypair and persist it.
      const kp = nacl.box.keyPair();
      cachedPriv = encodeBase64(kp.secretKey);
      cachedPub = encodeBase64(kp.publicKey);
      await SecureStore.setItemAsync(PRIV_KEY, cachedPriv);
      await SecureStore.setItemAsync(PUB_KEY, cachedPub);
    }

    // Publish the public key. The backend upsert is idempotent, so re-posting an
    // existing key is harmless and keeps the server in sync after a reinstall.
    // Failure here is non-fatal: peers just won't be able to message us via E2E
    // until a later publish succeeds.
    await api('/users/me/e2e-key', {
      method: 'POST',
      body: JSON.stringify({ publicKey: cachedPub }),
    }).catch(() => {});

    return cachedPub;
  } catch {
    return null;
  }
}

/**
 * Derive (and cache) the shared secret for a peer identified by user id or
 * username. Returns a 32-byte Uint8Array, or null if the peer has not enabled
 * E2E (no published key) or anything fails — in which case the caller falls back
 * to plaintext.
 */
export async function getSharedKey(userIdOrUsername: string): Promise<Uint8Array | null> {
  try {
    if (!userIdOrUsername) return null;
    const cached = sharedKeyCache.get(userIdOrUsername);
    if (cached) return cached;

    // We need our own private key to derive the secret.
    if (!cachedPriv) {
      cachedPriv = await SecureStore.getItemAsync(PRIV_KEY);
    }
    if (!cachedPriv) return null;

    const res = await api<{ userId?: string; publicKey?: string | null }>(
      `/users/${encodeURIComponent(userIdOrUsername)}/e2e-key`
    );
    const theirPub = res.success ? res.data?.publicKey : null;
    if (!theirPub) return null; // peer hasn't enabled E2E

    const shared = nacl.box.before(decodeBase64(theirPub), decodeBase64(cachedPriv));
    sharedKeyCache.set(userIdOrUsername, shared);
    return shared;
  } catch {
    return null;
  }
}

/**
 * Force a fresh key-agreement with a peer: drop the cached shared secret and
 * re-fetch their public key. Used by the "Couldn't decrypt — tap to retry" UX
 * in DMScreen: if the peer rotated keys (reinstall/new device) the cached
 * secret goes stale and every new message fails auth; a refresh + re-decrypt
 * recovers without restarting the app. Crash-safe: returns null on any failure
 * (same contract as getSharedKey).
 */
export async function refreshSharedKey(userIdOrUsername: string): Promise<Uint8Array | null> {
  try {
    sharedKeyCache.delete(userIdOrUsername);
  } catch {
    /* cache delete can't realistically throw; belt-and-braces */
  }
  return getSharedKey(userIdOrUsername);
}

/**
 * Encrypt `text` for a peer with their derived `shared` key, returning the
 * "e2e:v1:<nonce>:<ciphertext>" marker. Returns null on any failure so the
 * caller can fall back to sending plaintext.
 */
export function encrypt(text: string, shared: Uint8Array): string | null {
  try {
    const nonce = nacl.randomBytes(nacl.secretbox.nonceLength); // 24 bytes
    const ct = nacl.secretbox(decodeUTF8(text), nonce, shared);
    return `${MARKER_PREFIX}${encodeBase64(nonce)}:${encodeBase64(ct)}`;
  } catch {
    return null;
  }
}

/**
 * Structured outcome of a text decrypt. `success` carries the plaintext; a
 * failure carries a machine-readable `reason` (so the UI can decide between a
 * "waiting for key" spinner and a "couldn't decrypt — tap to retry" affordance)
 * plus a human-friendly `error` string for logging.
 *
 *   - 'not-encrypted' : input isn't an E2E marker (plaintext — caller passes through)
 *   - 'no-key'        : no shared key available yet (key-agreement pending / offline)
 *   - 'malformed'     : marker present but structurally invalid
 *   - 'auth-failed'   : ciphertext failed Poly1305 auth (key rotated / tampered)
 *   - 'error'         : unexpected exception (decode/runtime)
 */
export type DecryptFailureReason =
  | 'not-encrypted'
  | 'no-key'
  | 'malformed'
  | 'auth-failed'
  | 'error';

export type DecryptResult =
  | { success: true; plaintext: string }
  | { success: false; reason: DecryptFailureReason; error: string; retryable: boolean };

// Lightweight, PII-safe logger for the E2E path. Never logs plaintext or key
// material — only the failure reason + marker length, so a decrypt problem is
// diagnosable from device logs without leaking message content.
function logDecrypt(reason: DecryptFailureReason, markerLen: number): void {
  // 'not-encrypted' is the common plaintext path — don't spam logs with it.
  if (reason === 'not-encrypted') return;
  try {
    console.warn(`[e2e] decrypt failed: reason=${reason} markerLen=${markerLen}`);
  } catch {
    /* logging must never throw into a render path */
  }
}

/**
 * Decrypt an "e2e:v1:..." marker with the peer's `shared` key, returning a
 * STRUCTURED result ({ success, ... }) with a machine-readable failure reason and
 * a logged, retryable hint. This is the preferred API — the UI uses `reason` to
 * pick between a decrypting spinner (no-key) and a tap-to-retry bubble
 * (auth-failed, e.g. the peer rotated keys on reinstall).
 *
 * Crash-safe: any exception lands on { success:false, reason:'error' }.
 */
export function decryptResult(marker: string, shared: Uint8Array | null): DecryptResult {
  const markerLen = typeof marker === 'string' ? marker.length : 0;
  try {
    if (!isEncrypted(marker)) {
      return { success: false, reason: 'not-encrypted', error: 'input is not an E2E marker', retryable: false };
    }
    if (!shared) {
      // Key agreement hasn't resolved yet (or the peer hasn't published a key).
      // Retryable: a later key refresh can unlock this message.
      logDecrypt('no-key', markerLen);
      return { success: false, reason: 'no-key', error: 'no shared key available', retryable: true };
    }
    // "e2e:v1:<nonce>:<ct>" — split off the prefix, then nonce/ct on the colon.
    const rest = marker.slice(MARKER_PREFIX.length);
    const sep = rest.indexOf(':');
    if (sep === -1) {
      logDecrypt('malformed', markerLen);
      return { success: false, reason: 'malformed', error: 'marker missing nonce/ciphertext separator', retryable: false };
    }
    const nonceB64 = rest.slice(0, sep);
    const ctB64 = rest.slice(sep + 1);
    // Validate the nonce BEFORE decoding: a secretbox nonce is exactly 24 bytes,
    // which is 32 base64 chars (24 → ceil(24/3)*4). Reject anything malformed as a
    // structured 'malformed' failure rather than feeding garbage to decodeBase64 /
    // secretbox.open (a wrong-length nonce would just auth-fail confusingly, or a
    // non-base64 nonce would throw). Empty ciphertext is likewise malformed.
    if (!isValidNonceB64(nonceB64) || ctB64.length === 0) {
      logDecrypt('malformed', markerLen);
      return { success: false, reason: 'malformed', error: 'invalid nonce/ciphertext encoding', retryable: false };
    }
    const pt = nacl.secretbox.open(decodeBase64(ctB64), decodeBase64(nonceB64), shared);
    if (!pt) {
      // Poly1305 auth failed → wrong key (rotation) or tampered ciphertext.
      // Retryable: a fresh key agreement with the peer may recover it.
      logDecrypt('auth-failed', markerLen);
      return { success: false, reason: 'auth-failed', error: 'authentication failed (key rotated or tampered)', retryable: true };
    }
    return { success: true, plaintext: encodeUTF8(pt) };
  } catch (e) {
    logDecrypt('error', markerLen);
    return { success: false, reason: 'error', error: e instanceof Error ? e.message : 'decrypt exception', retryable: true };
  }
}

/**
 * Legacy string|null decrypt — thin wrapper over decryptResult() kept for
 * backward compatibility (returns the plaintext on success, else null). New code
 * should call decryptResult() to get the structured reason + retry hint.
 */
export function decrypt(marker: string, shared: Uint8Array): string | null {
  const r = decryptResult(marker, shared);
  return r.success ? r.plaintext : null;
}

// =============================================================================
// MEDIA E2E (images + audio) — encrypt file bytes client-side, upload an OPAQUE
// ciphertext blob, decrypt on display.
//
// Scheme mirrors text: NaCl secretbox(fileBytes, nonce, sharedKey). The
// ciphertext is uploaded via /uploads/encrypted (stored verbatim, never typed or
// scanned by the server). The real content type + nonce travel out-of-band in a
// message-attachment `description` marker — "e2emedia:v1:<contentType>:<nonceB64>"
// — so the server learns neither the media type nor the key material. On the
// receiving side we download the blob, secretbox.open it, write the plaintext to
// a local cache file, and hand the local uri to the existing image/audio render.
//
// GRACEFUL: every step is wrapped — any read/encrypt/upload/download/decrypt
// failure (INCLUDING the fs adapter being unavailable before pod install) returns
// null so callers fall back to the plaintext path or show a 🔒 placeholder. Never
// throws, never blocks sending/rendering.
// =============================================================================

// Skip encryption for very large files: reading/encrypting/base64-ing a huge blob
// in JS would spike memory and could OOM the app. ~20MB ceiling on the PLAINTEXT
// bytes (the original file). Callers fall back to the plaintext upload path.
const MAX_ENCRYPT_BYTES = 20 * 1024 * 1024;

const MEDIA_MARKER_PREFIX = 'e2emedia:v1:';

/**
 * Build the attachment `description` marker that flags an encrypted-media blob and
 * carries the metadata a peer needs to decrypt + render it: the REAL content type
 * (e.g. image/jpeg, audio/m4a) and the base64 nonce. Format:
 *   "e2emedia:v1:<realContentType>:<nonceB64>"
 */
export function mediaMarker(realContentType: string, nonceB64: string): string {
  return `${MEDIA_MARKER_PREFIX}${realContentType}:${nonceB64}`;
}

/**
 * Parse an attachment `description`. Returns { contentType, nonce } if it's one of
 * our encrypted-media markers, else null (plain attachment — render as today).
 */
export function parseMediaMarker(desc: unknown): { contentType: string; nonce: string } | null {
  try {
    if (typeof desc !== 'string' || !desc.startsWith(MEDIA_MARKER_PREFIX)) return null;
    const rest = desc.slice(MEDIA_MARKER_PREFIX.length);
    // "<contentType>:<nonceB64>" — content type itself contains a slash but no
    // colon, so split on the LAST colon to isolate the nonce.
    const sep = rest.lastIndexOf(':');
    if (sep === -1) return null;
    const contentType = rest.slice(0, sep);
    const nonce = rest.slice(sep + 1);
    if (!contentType || !nonce) return null;
    return { contentType, nonce };
  } catch {
    return null;
  }
}

// Map a real content type to a file extension for the decrypted local file, so the
// OS/players pick the right codec. Falls back to 'bin' for anything unknown.
function extForContentType(contentType: string): string {
  const ct = (contentType || '').toLowerCase();
  const MAP: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/heic': 'heic',
    'audio/m4a': 'm4a',
    'audio/mp4': 'm4a',
    'audio/aac': 'aac',
    'audio/mpeg': 'mp3',
    'audio/wav': 'wav',
    'audio/x-caf': 'caf',
    'audio/3gpp': '3gp',
  };
  if (MAP[ct]) return MAP[ct];
  // Generic fallback: take the subtype after the slash if it looks like an ext.
  const sub = ct.split('/')[1] || '';
  return /^[a-z0-9]{1,5}$/.test(sub) ? sub : 'bin';
}

// A short random hex token for unique temp filenames (avoids cache collisions
// across concurrent encrypt/decrypt of different files).
function randToken(): string {
  try {
    const b = getRandomBytes(8);
    let s = '';
    for (let i = 0; i < b.length; i++) s += b[i].toString(16).padStart(2, '0');
    return s;
  } catch {
    return `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
  }
}

/**
 * Encrypt the bytes of a local file and upload the ciphertext blob.
 *
 * Reads `localUri` as base64 → bytes, seals with secretbox(bytes, nonce, sharedKey),
 * writes the ciphertext to a temp `.bin` in the cache dir, and uploads it via
 * uploadEncrypted(). Returns { url, nonce(b64), contentType } on success, or null
 * on ANY failure (oversize / read / encrypt / upload / fs-unavailable) so the
 * caller falls back to the plaintext uploadMedia path.
 */
export async function encryptFileToUpload(
  localUri: string,
  realContentType: string,
  sharedKey: Uint8Array
): Promise<{ url: string; nonce: string; contentType: string } | null> {
  try {
    if (!localUri || !sharedKey) return null;

    // The cache dir is empty when no fs lib is present (pods not installed) →
    // bail to the plaintext path rather than fabricating a bad temp path.
    const cacheDir = FileSystem.cacheDirectory();
    if (!cacheDir) return null;

    // Read original bytes (base64) — the only async file read in the encrypt path.
    const b64 = await FileSystem.readAsStringAsync(localUri, {
      encoding: FileSystem.EncodingType.Base64,
    });
    if (!b64) return null;
    const bytes = decodeBase64(b64);

    // Guard large files: skip encryption (caller falls back to plaintext) so we
    // never OOM the JS heap doing in-memory crypto on a huge blob.
    if (bytes.length > MAX_ENCRYPT_BYTES) {
      console.warn(
        `[e2e] media ${bytes.length} bytes exceeds ${MAX_ENCRYPT_BYTES} cap; skipping encryption (plaintext fallback)`
      );
      return null;
    }

    const nonce = nacl.randomBytes(nacl.secretbox.nonceLength); // 24 bytes
    const ct = nacl.secretbox(bytes, nonce, sharedKey);
    if (!ct) return null;

    // Persist ciphertext to a temp file so we can multipart-upload it by uri.
    const tempUri = `${cacheDir}enc_${randToken()}.bin`;
    const wrote = await FileSystem.writeAsStringAsync(tempUri, encodeBase64(ct), {
      encoding: FileSystem.EncodingType.Base64,
    });
    if (!wrote) return null;

    const url = await uploadEncrypted(tempUri);

    // Best-effort cleanup of the temp ciphertext (non-fatal if it fails).
    FileSystem.deleteAsync(tempUri, { idempotent: true }).catch(() => {});

    if (!url) return null;
    return { url, nonce: encodeBase64(nonce), contentType: realContentType };
  } catch {
    return null;
  }
}

// Cache decrypted blobs by ciphertext url so re-renders (FlatList recycling, state
// updates) don't re-download + re-decrypt the same media every frame.
const decryptedCache = new Map<string, string>();

/**
 * Download an encrypted-media blob, decrypt it, and materialise the plaintext to a
 * local cache file. Returns the local file uri (ready for <Image>/AudioBubble), or
 * null on ANY failure (download / read / auth-fail / decrypt / fs-unavailable) —
 * caller then shows the 🔒 placeholder. Result is cached by `url` so it's
 * downloaded/decrypted once.
 */
export async function decryptFileToLocal(
  url: string,
  nonceB64: string,
  sharedKey: Uint8Array,
  realContentType: string
): Promise<string | null> {
  try {
    if (!url || !nonceB64 || !sharedKey) return null;

    const cached = decryptedCache.get(url);
    if (cached) return cached;

    const cacheDir = FileSystem.cacheDirectory();
    if (!cacheDir) return null;

    // Download the ciphertext blob to a temp file.
    const dlUri = `${cacheDir}dl_${randToken()}`;
    const dl = await FileSystem.downloadAsync(url, dlUri);
    if (!dl || dl.status >= 400) {
      FileSystem.deleteAsync(dlUri, { idempotent: true }).catch(() => {});
      return null;
    }

    // Read ciphertext bytes and authenticate-decrypt.
    const ctB64 = await FileSystem.readAsStringAsync(dl.uri, {
      encoding: FileSystem.EncodingType.Base64,
    });
    FileSystem.deleteAsync(dl.uri, { idempotent: true }).catch(() => {});
    if (!ctB64) return null;

    const pt = nacl.secretbox.open(decodeBase64(ctB64), decodeBase64(nonceB64), sharedKey);
    if (!pt) return null; // wrong key / tampered → caller shows 🔒

    // Write plaintext with the correct extension so players/viewers pick it up.
    const ext = extForContentType(realContentType);
    const outUri = `${cacheDir}dec_${randToken()}.${ext}`;
    const wrote = await FileSystem.writeAsStringAsync(outUri, encodeBase64(pt), {
      encoding: FileSystem.EncodingType.Base64,
    });
    if (!wrote) return null;

    decryptedCache.set(url, outUri);
    return outUri;
  } catch {
    return null;
  }
}
